import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";

export enum PartTimelineSource {
  Part,
  Offer,
  OrderHistory,
}

export const partTimelineSourceLabels: Record<PartTimelineSource, string> = {
  [PartTimelineSource.Part]: "Part",
  [PartTimelineSource.Offer]: "Listing",
  [PartTimelineSource.OrderHistory]: "Order",
};

export const partTimelineSourceColours: Record<PartTimelineSource, string> = {
  [PartTimelineSource.Part]: "bg-slate-100 text-slate-700",
  [PartTimelineSource.Offer]: "bg-indigo-100 text-indigo-700",
  [PartTimelineSource.OrderHistory]: "bg-emerald-100 text-emerald-700",
};

export enum PartOrderStatus {
  Sold,
  Picked,
  Shipped,
  Cancelled,
  Returned,
}

export interface IPartTimelineChange {
  field: string;
  old: string | null;
  new: string | null;
}

export interface IPartTimelineEvent {
  id: string;
  timestamp: string;
  source: PartTimelineSource;
  entityName: string;
  entityId: string;
  isActiveOffer: boolean;
  action: string;
  userId: string | null;
  milestones: string[];
  isKeyEvent: boolean;
  changes: IPartTimelineChange[];
  orderNumber: string | null;
  orderStatus: PartOrderStatus | null;
  soldDate: string | null;
  returnedOn: string | null;
}

export interface IPartTimelineOffer {
  id: string;
  sku: string | null;
  ebayId: string | null;
  isActive: boolean;
  publishedOn: string | null;
  listingStarted: string | null;
  listingEnded: string | null;
  created: string;
}

export interface IPartTimelineEbayIdentity {
  ebayItemNumber: string | null;
  ebayTradingApiItemId: string | null;
  ebayInventoryApiOfferId: string | null;
  activeOfferEbayId: string | null;
  itemIdMismatch: boolean;
}

export interface IPartTimeline {
  partDataId: string;
  tagNumber: string | null;
  activePartOfferId: string;
  saleCount: number;
  returnCount: number;
  soldMoreThanOnce: boolean;
  ebayIdentity: IPartTimelineEbayIdentity;
  offers: IPartTimelineOffer[];
  events: IPartTimelineEvent[];
}

interface IPartTimelineResponse {
  success: boolean;
  message: string;
  data: IPartTimeline;
}

// Both the Stock drawer and the Active Listings history card open on key events only and let the
// user unfold the rest, so the filter lives here rather than being kept in step in two components.
export function useTimelineEvents(events: IPartTimelineEvent[] | undefined, keyEventsOnly: boolean) {
  return useMemo(() => {
    const allEvents = events ?? [];
    return keyEventsOnly ? allEvents.filter((x) => x.isKeyEvent) : allEvents;
  }, [events, keyEventsOnly]);
}

export default function usePartTimeline(partId: string | undefined, componentName = "usePartTimeline") {
  const { get } = useApiHelper(apiConfig(componentName));

  const partTimeline = useQuery({
    queryKey: ["partTimeline", partId],
    queryFn: () => get<IPartTimelineResponse>(`/part/${partId}/timeline`),
    enabled: !!partId,
    refetchOnWindowFocus: false,
  });

  return { partTimeline };
}
